import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { Session } from '@supabase/supabase-js';
import { apiRequest } from '../lib/api';
import { getSupabase } from '../lib/auth';
import { CareStoreEngine, getDogRole, type CareUser } from '../lib/careStoreEngine';
import type { CareCommand, CareState, CommandResult } from '../domain/types';

export function useCareStore() {
  const supabase = getSupabase();
  const [auth, setAuth] = useState<{ loaded: boolean; session: Session | null }>({ loaded: !supabase, session: null });
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [deletionResult,setDeletionResult]=useState<'completed'|'pending'|null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const user: CareUser | null = useMemo(() => auth.session ? { id: auth.session.user.id, email: auth.session.user.email, name: typeof auth.session.user.user_metadata?.name === 'string' ? auth.session.user.user_metadata.name : undefined } : null, [auth.session]);
  const mode = 'connected' as const;
  const engine = useMemo(() => new CareStoreEngine(user ? `user:${user.id}` : 'signed-out', 'connected', user, user ? { fetchState: () => apiRequest<CareState>('/state'), sendCommand: command => apiRequest<CommandResult>('/commands', { method: 'POST', body: JSON.stringify(command) }) } : undefined), [user?.id]);
  const latestEngine=useRef(engine);latestEngine.current=engine;
  const snapshot = useSyncExternalStore(engine.subscribe, engine.getSnapshot, engine.getSnapshot);

  useEffect(() => {
    if (!supabase) return;
    let current = true;
    void supabase.auth.getSession().then(({ data, error }) => { if (current) { setAuth({ loaded: true, session: data.session }); if (error) setAuthError(error.message); } });
    const { data } = supabase.auth.onAuthStateChange((event, session) => { if(current){if(event==='SIGNED_OUT')void latestEngine.current.clear().catch(()=>undefined);if(event==='SIGNED_IN'&&session)setDeletionResult(null);setAuth({loaded:true,session});} });
    return () => { current = false; data.subscription.unsubscribe(); };
  }, [supabase]);
  useEffect(() => { if (auth.loaded && user) void engine.initialize(); return () => engine.dispose(); }, [engine, auth.loaded, user?.id]);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update); window.addEventListener('offline', update);
    return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update); };
  }, []);
  useEffect(() => { engine.setOnline(online); }, [engine, online]);

  useEffect(() => {
    if (!user || !supabase) return;
    let alive = true;
    // Messages are hints only; records always come through an authorized state read.
    const stateHint = () => { if (alive) void engine.refresh(); };
    const grantHint = (payload: { new: unknown }) => {
        if (!alive) return;
        const row = payload.new as { dog_id?: string; status?: string; expires_at?: string };
        if (row.dog_id && (row.status === 'revoked' || row.expires_at && Date.parse(row.expires_at) <= Date.now())) void engine.revokeDog(row.dog_id);
        else void engine.refresh();
      };
    const channel = supabase.channel(`care-${user.id}-${crypto.randomUUID()}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sync_revisions' }, stateHint)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'sync_revisions' }, stateHint)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'dog_access', filter: `user_id=eq.${user.id}` }, grantHint)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'dog_access', filter: `user_id=eq.${user.id}` }, grantHint)
      .subscribe(status => { if (alive && status === 'SUBSCRIBED') void engine.refresh(); });
    const timer = window.setInterval(() => { if (navigator.onLine && document.visibilityState === 'visible') void engine.refresh(); }, 30_000);
    const focus = () => { if (navigator.onLine) void engine.refresh(); };
    window.addEventListener('focus', focus);
    return () => { alive = false; clearInterval(timer); window.removeEventListener('focus', focus); void supabase.removeChannel(channel); };
  }, [engine, user?.id, supabase]);

  const signIn = useCallback(async (email: string, options?: { createAccount?: boolean; name?: string }) => {
    if (!supabase) throw new Error('This build is running locally. Configure Supabase to connect a shared care account.');
    const normalized = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) throw new Error('Enter a valid email address.');
    const { error } = await supabase.auth.signInWithOtp({ email: normalized, options: { shouldCreateUser: options?.createAccount ?? false, ...(options?.createAccount && options.name?.trim() ? { data: { name: options.name.trim() } } : {}) } });
    if (error) { setAuthError(error.message); throw error; }
    setAuthError(null);
  }, [supabase]);
  const verifyCode = useCallback(async (email: string, code: string) => {
    if (!supabase) throw new Error('Shared care accounts are not configured yet.');
    const { error } = await supabase.auth.verifyOtp({ email: email.trim().toLowerCase(), token: code.trim(), type: 'email' });
    if (error) { setAuthError(error.message); throw error; }
    setAuthError(null);
  }, [supabase]);
  const signOut = useCallback(async () => {
    if (mode === 'connected') await engine.clear();
    if (supabase) { const { error } = await supabase.auth.signOut(); if (error) { await engine.initialize(); throw error; } }
    setAuth({ loaded: true, session: null });
  }, [engine, mode, supabase]);
  const deleteAccount = async () => {
    if(!user||!online)throw new Error('Sign in and connect to the internet to delete your account.');
    const result=await apiRequest<{accepted:boolean;status:'completed'|'pending'}>('/account/delete',{method:'POST',body:JSON.stringify({email:user.email,confirmation:'DELETE'})});
    if(!result.accepted)throw new Error('The deletion request could not be confirmed.');
    setDeletionResult(result.status);
    // Clear this device even if the now-deleted server session cannot sign out remotely.
    await engine.clear().catch(()=>undefined);
    await supabase?.auth.signOut({scope:'local'}).catch(()=>undefined);
    setAuth({loaded:true,session:null});
  };
  const deleteDog = async (dogId: string, name: string, revision: number) => {
    if (!user || !online) throw new Error('Connect to the internet before deleting a dog.');
    const current = engine.getSnapshot();
    if (current.pendingCount || current.conflicts.length) throw new Error('Sync pending changes and resolve conflicts before deleting a dog.');
    if (getDogRole(current.state, dogId, user, mode) !== 'owner') throw new Error('Only the owner can delete this dog.');
    const result = await apiRequest<{deleted: boolean}>(`/dogs/${dogId}/delete`, {method:'POST',body:JSON.stringify({name,revision})});
    if (!result.deleted) throw new Error('Deletion could not be confirmed.');
    await engine.revokeDog(dogId, true);
  };
  const role = getDogRole(snapshot.state, snapshot.state.selectedDogId, user, mode);
  return { ...snapshot, authReady: auth.loaded, ready: auth.loaded && snapshot.ready, mode, role, user, online, syncError: authError || snapshot.syncError, deleteAccount, deletionResult, deleteDog, dispatch: (command: CareCommand) => user ? engine.dispatch(command) : Promise.reject(new Error('Sign in before recording care.')), signIn, verifyCode, signOut, refresh: () => engine.refresh(), selectDog: (dogId: string) => engine.selectDog(dogId), resolveConflict: (commandId: string, action: 'discard' | 'retry') => engine.resolveConflict(commandId, action), configured: !!supabase };
}
